import { Controller } from "@hotwired/stimulus"
import { pressable } from "../lib/press"

// The "which certificate?" step of the wizard: each live template is shown as
// a scaled-down live render of the real certificate.
//
// Frames stay empty until the operator is on the step, so an organization
// with several certificates doesn't render all of them for nothing.
//
// The wildlife pickers (gorilla family, chimpanzee community) sit in a box
// under the cards that unfolds when the operator picks a template naming
// their species — each radio lists those in data-tracks — from the parks of
// the template's country (data-country on both). A picker for another
// species or country is hidden and its chips disabled so nothing it holds
// is submitted. The dates step's calendar follows the pick too: a single-day
// certificate (data-single-day) asks for one day, the rest for a range.
export default class extends Controller {
  static targets = ["frame", "check", "wildlife", "wildlifeBox", "wildlifeCard", "wildlifeEyebrow", "templateName", "choice", "chooseHeading", "askHeading", "datesPrompt", "datesHeading"]

  connect() {
    this.unpress = pressable(this.element, "label[data-certificate-choice], .wildlife-chip-label", { scale: 0.96 })

    this.resizeObserver = new ResizeObserver(() => this.scaleFrames())
    this.frameTargets.forEach(frame => {
      this.resizeObserver.observe(frame)
      const iframe = frame.querySelector("iframe")
      iframe?.addEventListener("load", () => { if (iframe.getAttribute("src")) frame.classList.remove("frame-loading") })
    })
    this.scaleFrames()

    this.element.addEventListener("change", this.markChecked)
    this.markChecked()

    // Restored straight onto this step (a reload mid-wizard) — load right away.
    if (this.frameTargets.some(frame => frame.closest("[data-wizard-target='step']")?.classList.contains("hidden") === false)) {
      this.refresh()
    }
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.element.removeEventListener("change", this.markChecked)
    this.unpress?.()
  }

  // The wizard has settled on a step: if it is this one, render the previews
  // now rather than while the panel was still fading in.
  entered(event) {
    if (this.frameTargets.some(frame => event.target.contains(frame))) this.refresh()
  }

  // Points every preview at what has been answered so far.
  refresh() {
    const params = new URLSearchParams()
    for (const [param, field] of Object.entries(this.fields)) {
      const value = field?.value?.trim()
      if (value) params.set(param, value)
    }

    this.frameTargets.forEach(frame => {
      const iframe = frame.querySelector("iframe")
      if (!iframe) return
      const url = `${iframe.dataset.src}?${params}`
      if (iframe.getAttribute("src") !== url) {
        frame.classList.add("frame-loading")
        iframe.setAttribute("src", url)
      }
    })
  }

  // Name, dates and whichever group is picked among the pickers in play.
  get fields() {
    const form = this.element
    const fields = {
      traveller_name: form.querySelector("[name='certificate[traveller_name]']"),
      arrival_date: form.querySelector("[name='certificate[arrival_date]']"),
      departure_date: form.querySelector("[name='certificate[departure_date]']")
    }
    form.querySelectorAll("[data-wildlife-picker] input[type='radio']:checked:not(:disabled)").forEach(radio => {
      const param = radio.name.match(/^certificate\[(\w+)\]$/)?.[1]
      if (param) fields[param] = radio
    })
    return fields
  }

  // A4 at 595pt is ~793px, far wider than a card; shrink the iframe to the
  // card's width so the whole page shows.
  scaleFrames() {
    this.frameTargets.forEach(frame => {
      const iframe = frame.querySelector("iframe")
      if (!iframe) return
      const pageWidthPx = parseFloat(frame.dataset.pageWidth) * (96 / 72)
      const scale = frame.clientWidth / pageWidthPx
      if (Number.isFinite(scale) && scale > 0) iframe.style.transform = `scale(${scale})`
    })
  }

  // Tailwind's peer-checked: only reaches the radio's siblings, and the tick
  // sits deeper than that.
  markChecked = (event) => {
    this.element.querySelectorAll("input[name='certificate[certificate_template_id]']").forEach(radio => {
      const label = radio.closest("label")
      const check = label?.querySelector("[data-certificate-picker-target='check']")
      if (check) check.classList.toggle("bg-primary", radio.checked)
      label?.toggleAttribute("data-checked", radio.checked)
    })
    this.syncWildlife()
    this.syncDates()
    // A group picked shows on the previews too.
    if (event?.target?.matches?.("[data-wildlife-picker] input[type='radio']")) this.refresh()
  }

  // A shortcut folded the choice away behind the family question; this
  // brings the cards back.
  showChoice() {
    if (this.hasChoiceTarget) this.choiceTarget.hidden = false
    if (this.hasAskHeadingTarget) this.askHeadingTarget.hidden = true
    if (this.hasChooseHeadingTarget) this.chooseHeadingTarget.hidden = false
    if (this.hasWildlifeCardTarget) {
      this.wildlifeCardTarget.classList.add("mt-5")
      this.wildlifeCardTarget.removeAttribute("data-asked")
    }
    if (this.hasWildlifeEyebrowTarget) this.wildlifeEyebrowTarget.hidden = false
    this.refresh()
    this.element.querySelector("input[name='certificate[certificate_template_id]']:checked")?.focus({ preventScroll: true })
  }

  // The calendar is told through its Stimulus value, so it rebuilds itself;
  // the prompt under the dates heading and the wildlife eyebrow name the pick.
  syncDates() {
    const chosen = this.element.querySelector("input[name='certificate[certificate_template_id]']:checked")
    if (!chosen) return
    this.templateNameTargets.forEach(el => el.textContent = chosen.dataset.templateName)
    // A single-day certificate names its day in the heading, so the line
    // under it would only say the same thing again.
    const single = chosen.dataset.singleDay === "true"
    if (this.hasDatesHeadingTarget) this.datesHeadingTarget.textContent = chosen.dataset.datesHeading
    if (this.hasDatesPromptTarget) {
      this.datesPromptTarget.textContent = chosen.dataset.datesPrompt || ""
      this.datesPromptTarget.hidden = single || !chosen.dataset.datesPrompt
    }
    const picker = this.element.querySelector("[data-controller~='date-range-picker']")
    if (picker) picker.dataset.dateRangePickerModeValue = single ? "single" : "range"
  }

  syncWildlife() {
    const chosen = this.element.querySelector("input[name='certificate[certificate_template_id]']:checked")
    const tracked = chosen ? chosen.dataset.tracks.split(" ").filter(Boolean) : []
    const shown = this.wildlifeTargets.filter(picker => {
      const on = tracked.includes(picker.dataset.wildlifePicker) && picker.dataset.country === chosen.dataset.country
      picker.classList.toggle("hidden", !on)
      picker.querySelectorAll("input[type='radio']").forEach(radio => radio.disabled = !on)
      return on
    })
    if (this.hasWildlifeBoxTarget) {
      const box = this.wildlifeBoxTarget
      box.toggleAttribute("data-open", shown.length > 0)
      box.toggleAttribute("inert", shown.length === 0)
    }
  }
}
